// UF-01 (U21): "Packaging used" on a run. Pure sums; the reads live in
// RunPackagingCard. No ledger write, no migration.

export interface PalletIssue { pallet_id: string; stock_item_id: string; quantity: number }
export interface MovementRow { movement_id: string; item_id: string; quantity: number; reference: string | null }
export interface PackagingLine { item_id: string; onPallets: number; byHand: number; total: number }

/** LIKE-escape so a run number with % or _ matches only itself. */
export function escapeLike(s: string): string {
  return s.replace(/[\\%_]/g, c => `\\${c}`)
}

/**
 * On pallets = the pallet_stock_issues of the run's LIVE pallets, less the
 * auto-issue movements that were voided (a void adds an ADJUST with reference
 * 'void:<id>' and leaves the pallet_stock_issues row behind).
 * By hand = the run-text ISSUE movements that were not voided.
 * Build-up pallets count in full on the run in pallets.run_id.
 */
export function sumRunPackaging(
  issues: PalletIssue[],
  autoMovements: MovementRow[],     // ISSUE 'Pallet pack (auto)' movements, reference = pallet id
  handMovements: MovementRow[],     // ISSUE 'Issued to run <n>' movements
  voidedIds: ReadonlySet<string>,   // movement ids that have a 'void:<id>' row
): PackagingLine[] {
  const voidedAuto = new Map<string, number>()   // pallet|item -> qty voided
  for (const m of autoMovements) {
    if (!voidedIds.has(m.movement_id) || !m.reference) continue
    const k = `${m.reference}|${m.item_id}`
    voidedAuto.set(k, (voidedAuto.get(k) ?? 0) + Math.abs(Number(m.quantity)))
  }
  const acc = new Map<string, { onPallets: number; byHand: number }>()
  const slot = (id: string) => {
    let s = acc.get(id)
    if (!s) { s = { onPallets: 0, byHand: 0 }; acc.set(id, s) }
    return s
  }
  for (const i of issues) {
    const net = Math.max(0, Number(i.quantity) - (voidedAuto.get(`${i.pallet_id}|${i.stock_item_id}`) ?? 0))
    if (net > 0) slot(i.stock_item_id).onPallets += net
  }
  for (const m of handMovements) {
    if (voidedIds.has(m.movement_id)) continue
    const q = Math.abs(Number(m.quantity))
    if (q > 0) slot(m.item_id).byHand += q
  }
  return [...acc.entries()].map(([item_id, s]) => ({ item_id, ...s, total: s.onPallets + s.byHand }))
}
